import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useTranslation } from "react-i18next";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

interface AssigneeSelectorProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

interface UserInfo {
  user_id: string;
  first_name: string;
  last_name: string;
  active: boolean;
  can_see_details: boolean;
}

export const AssigneeSelector = ({ 
  value, 
  onValueChange, 
  placeholder,
  disabled = false 
}: AssigneeSelectorProps) => {
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();
  const [orgUsers, setOrgUsers] = useState<UserInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrgUsers = async () => {
      if (!currentOrganization?.id) return;
      
      setIsLoading(true);
      setFetchError(null);
      
      try {
        // Use the database function that provides full access to user names
        const { data, error } = await supabase
          .rpc('get_organization_users_for_assignee', {
            p_organization_uuid: currentOrganization.id
          });
        
        if (error) {
          console.error("Error fetching organization users:", error);
          setFetchError(error.message || "Failed to load users");
        } else if (data) {
          setOrgUsers(data);
        }
      } catch (error: any) {
        console.error("Error fetching organization users:", error);
        setFetchError(error.message || "Failed to load users");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchOrgUsers();
  }, [currentOrganization?.id]);

  const getDisplayName = (user: UserInfo) => {
    return `${user.first_name} ${user.last_name}`.trim();
  };

  // Ensure value is a string and find the selected user
  const stringValue = String(value || '');
  const selectedUser = orgUsers.find(user => user.user_id === stringValue);
  
  // If we have a value but no selected user, try to fetch that specific user's info
  const [selectedUserInfo, setSelectedUserInfo] = useState<UserInfo | null>(null);
  
  useEffect(() => {
    const fetchSelectedUserInfo = async () => {
      if (stringValue && !selectedUser && !isLoading) {
        try {
          const { data, error } = await supabase
            .rpc('get_user_display_info', {
              p_user_id: stringValue
            });
          
          if (!error && data && data.length > 0) {
            setSelectedUserInfo(data[0]);
          }
        } catch (error) {
          console.warn("Could not fetch selected user info:", error);
        }
      } else {
        setSelectedUserInfo(null);
      }
    };
    
    fetchSelectedUserInfo();
  }, [stringValue, selectedUser, isLoading]);
  
  // Determine display value
  const displayValue = selectedUser ? getDisplayName(selectedUser) : 
    (selectedUserInfo ? getDisplayName(selectedUserInfo) : 
     (stringValue && !isLoading ? `User ${stringValue.substring(0, 8)}` : undefined));

  // If we're still loading and have a value, show a loading state
  if (isLoading && stringValue) {
    return (
      <Select value="" onValueChange={onValueChange} disabled={true}>
        <SelectTrigger className="">
          <SelectValue placeholder="Loading..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="loading" disabled>
            {t("common.loading")}
          </SelectItem>
        </SelectContent>
      </Select>
    );
  }

  return (
    <div className="space-y-1">
      <Select value={stringValue} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger className="">
          {displayValue || (placeholder || "---")}
        </SelectTrigger>
        <SelectContent>
          {isLoading ? (
            <SelectItem value="loading" disabled>
              {t("common.loading")}
            </SelectItem>
          ) : orgUsers.length === 0 ? (
            <SelectItem value="no-users" disabled>
              {t("jobs.formWizard.noRecruiters", "No users found in your organization")}
            </SelectItem>
          ) : (
            orgUsers.map(user => (
              <SelectItem key={user.user_id} value={user.user_id}>
                {getDisplayName(user)}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {fetchError && (
        <p className="text-xs text-amber-600 mt-1">
          {fetchError}
        </p>
      )}
    </div>
  );
}; 